import CancelModalButton from 'components/Custom/CancelModalButton'
import CustomModalFooter from 'components/Custom/CustomModalFooter'
import CustomModalHeader from 'components/Custom/CustomModalHeader'
import { useEffect, useState, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import {
  Card,
  FormGroup,
  Input,
  Modal,
  ModalBody,
  NavLink,
  Button,
} from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faDownload,
  faEye,
  faFile,
  faFileExcel,
  faFilePdf,
  faFileWord,
} from '@fortawesome/free-solid-svg-icons'
import { fetchSubmission } from 'store/Forms/actions'
import { clientBaseUrl } from 'utils/AxiosInstance'
import {
  generateFormSubmissionPDF,
  generateSimplePDF,
} from 'utils/pdfGenerator'
import SubmissionComments from './SubmissionComments'
import Select from '../Select'
import { setShowFormSubmission } from 'store/Forms/actions'
import { setFormSubmissionId } from 'store/Forms/actions'

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg']

const getFileExtension = (path) => {
  const cleanPath = (path || '').split('?')[0].split('#')[0]
  const name = cleanPath.substring(cleanPath.lastIndexOf('/') + 1)
  const dotIndex = name.lastIndexOf('.')
  return dotIndex > -1 ? name.substring(dotIndex + 1).toLowerCase() : ''
}

const getFileName = (path) => {
  const cleanPath = (path || '').split('?')[0].split('#')[0]
  const name = cleanPath.substring(cleanPath.lastIndexOf('/') + 1)
  try {
    return decodeURIComponent(name) || 'file'
  } catch (e) {
    return name || 'file'
  }
}

const getFileIcon = (extension) => {
  if (extension === 'pdf') return faFilePdf
  if (['doc', 'docx'].includes(extension)) return faFileWord
  if (['xls', 'xlsx', 'csv'].includes(extension)) return faFileExcel
  return faFile
}

// Renders images inline; any other file type (or an image that fails to load)
// is shown as a card with buttons to open it in a new tab or download it.
const UploadedFile = ({ path, alt }) => {
  const [imageFailed, setImageFailed] = useState(false)
  const extension = getFileExtension(path)
  const fileName = getFileName(path)
  const isKnownNonImage = extension && !IMAGE_EXTENSIONS.includes(extension)

  if (!isKnownNonImage && !imageFailed) {
    return (
      <img
        src={path}
        alt={alt}
        style={{ maxWidth: '100%', height: 'auto' }}
        onError={() => setImageFailed(true)}
      />
    )
  }

  const handleDownload = () => {
    const proxyUrl = `${clientBaseUrl()}forms/proxy-file?url=${encodeURIComponent(
      path
    )}`
    const link = document.createElement('a')
    link.href = proxyUrl
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div
      className='p-2 border rounded d-flex align-items-center flex-wrap'
      style={{ backgroundColor: '#f8f9fa', gap: '0.5rem' }}
    >
      <FontAwesomeIcon icon={getFileIcon(extension)} size='lg' />
      <small style={{ wordBreak: 'break-all' }}>
        <strong>File:</strong> {fileName}
      </small>
      <button
        type='button'
        onClick={() => window.open(path, '_blank', 'noopener,noreferrer')}
        className='btn btn-info py-1 ml-2'
      >
        View <FontAwesomeIcon icon={faEye} />
      </button>
      <button
        type='button'
        onClick={handleDownload}
        className='btn btn-success py-1'
      >
        Download <FontAwesomeIcon icon={faDownload} />
      </button>
    </div>
  )
}

function ViewSubmissionModal({
  setShowViewSubmissionModal,
  closeModalFn,
  showViewSubmissionModal,
  submissionId,
  userId,
  activeTab,
  setActiveTab,
  children,
}) {
  const dispatch = useDispatch()
  const { newFormFields, files, showFormSubmission } = useSelector(
    (store) => store.form
  )
  const formRef = useRef(null)
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)

  useEffect(() => {
    dispatch(fetchSubmission(submissionId, userId))
  }, [dispatch, submissionId, userId])

  const handleDownloadPDF = async () => {
    if (!formRef.current) {
      alert('Form content not found. Please try again.')
      return
    }

    // Ensure we're on the Form tab before generating PDF
    if (activeTab !== 0) {
      setActiveTab(0)
      // Wait a moment for tab switch to complete
      await new Promise((resolve) => setTimeout(resolve, 200))
    }

    // Double-check that the form ref is still valid after tab switch
    if (!formRef.current) {
      alert('Form content not found after tab switch. Please try again.')
      return
    }

    setIsGeneratingPDF(true)
    try {
      console.log('Form element for PDF:', formRef.current)

      // First try the advanced PDF generation
      let success = await generateFormSubmissionPDF(
        submissionId,
        formRef.current
      )

      // If that fails, try the simple method
      if (!success) {
        console.log('Advanced PDF failed, trying simple method...')
        success = await generateSimplePDF(submissionId, formRef.current)
      }

      if (!success) {
        alert('Failed to generate PDF. Please try again.')
      }
    } catch (error) {
      console.error('PDF generation error:', error)

      // Try the simple method as a last resort
      try {
        console.log('Trying simple PDF as fallback...')
        const success = await generateSimplePDF(submissionId, formRef.current)
        if (!success) {
          alert('Failed to generate PDF. Please try again.')
        }
      } catch (fallbackError) {
        console.error('Fallback PDF generation also failed:', fallbackError)
        alert('Failed to generate PDF. Please try again.')
      }
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  const renderBody = (field) => {
    switch (field.type) {
      case 'heading':
        return <h2 className='border-bottom border-dark'>{field.text}</h2>
      case 'paragraph':
        return <p>{field.text}</p>
      case 'text':
      case 'number':
      case 'textarea':
      case 'date':
        return (
          <FormGroup className='ml-0 mb-0'>
            <label className='form-control-label' htmlFor='text'>
              {field.label}{' '}
              {field.required ? <span className='text-danger'>*</span> : null}
            </label>
            <Input
              key={field?.index}
              readOnly={true}
              disabled={field?.disabled}
              placeholder={field?.placeholder}
              value={field?.answer}
              id='text'
              type={
                field.inputType
                  ? field.inputType
                  : field.type === 'number'
                  ? 'number'
                  : field.type === 'text'
                  ? 'text'
                  : field.type === 'date'
                  ? 'date'
                  : 'textarea'
              }
              style={{
                height: field.type === 'textarea' ? 'auto' : '38px',
                resize: 'vertical',
              }}
              name='text'
              rows={field?.rows}
            />
            {field.helpText ? (
              <p style={{ fontSize: '14px' }}>{field.helpText}</p>
            ) : null}
          </FormGroup>
        )
      case 'checkbox':
      case 'radio':
        return (
          <div className='d-flex flex-column' style={{ gap: '0.5rem' }}>
            <label className='form-control-label' htmlFor='text'>
              {field.label}{' '}
              {field.required ? <span className='text-danger'>*</span> : null}
            </label>
            {field?.options?.map((option, index) => (
              <>
                {field.type === 'checkbox' ? (
                  <div
                    className='custom-control custom-checkbox'
                    style={{ zIndex: '0' }}
                  >
                    <input
                      className='custom-control-input'
                      id={`${field.index}${index}`}
                      type='checkbox'
                      checked={option.checked}
                      disabled={true}
                    />
                    <label
                      className='custom-control-label'
                      htmlFor={`${field.index}${index}`}
                    >
                      {option.label}
                    </label>
                  </div>
                ) : (
                  <div
                    className='d-flex align-items-center'
                    style={{ zIndex: 0 }}
                  >
                    <input
                      id={`radio-${option.label}`}
                      type='radio'
                      checked={option.checked}
                      style={{ marginRight: '0.5rem' }}
                    />
                    <label htmlFor={`radio-${option.label}`} className='mb-0'>
                      {option.label}
                    </label>
                  </div>
                )}
              </>
            ))}
            {field.helpText ? (
              <p style={{ fontSize: '14px' }}>{field.helpText}</p>
            ) : null}
          </div>
        )
      case 'signature':
        const signatureFile =
          files &&
          files.length &&
          files.find((file) => file.field_id === field.index)
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'start',
            }}
          >
            <label
              role='button'
              className='form-control-label'
              style={{ marginBottom: '8px' }}
            >
              {field.label}{' '}
              {field.required ? <span className='text-danger'>*</span> : null}
            </label>
            {signatureFile ? (
              <img
                src={signatureFile.path}
                alt={field.label || 'Signature'}
                style={{ maxWidth: '100%', height: 'auto' }}
                crossOrigin='anonymous'
              />
            ) : null}
            {field.helpText ? (
              <p style={{ fontSize: '14px' }}>{field.helpText}</p>
            ) : null}
          </div>
        )
      case 'upload':
        if (field.inputType === 'attach') {
          // Admin-attached file - show view and download buttons only
          return (
            <div className='ml-0 mb-0'>
              <label
                role='button'
                className='form-control-label'
                style={{ marginBottom: '8px' }}
              >
                {field.label}{' '}
                {field.required ? <span className='text-danger'>*</span> : null}
              </label>
              {field?.fileUrl && field?.selectedFileName ? (
                <div
                  className='mt-2 p-2 border rounded'
                  style={{ backgroundColor: '#f8f9fa' }}
                >
                  <button
                    onClick={() => {
                      window.open(field?.fileUrl, '_blank')
                    }}
                    className='btn btn-info py-1 mr-3'
                  >
                    View <FontAwesomeIcon icon={faEye} />
                  </button>
                  <button
                    onClick={() => {
                      if (!field?.fileUrl) {
                        alert('No file URL available for download')
                        return
                      }

                      const url = clientBaseUrl()
                      const fileUrl = encodeURIComponent(field?.fileUrl)
                      const proxyUrl = `${url}forms/proxy-file?url=${fileUrl}`

                      const link = document.createElement('a')
                      link.href = proxyUrl
                      link.download =
                        field.selectedFileName || 'downloaded-file'
                      document.body.appendChild(link)
                      link.click()
                      document.body.removeChild(link)
                    }}
                    className='btn btn-success py-1 mr-3'
                  >
                    Download <FontAwesomeIcon icon={faDownload} />
                  </button>
                  <small className='text-success'>
                    <strong>File:</strong> {field.selectedFileName}
                  </small>
                  {/* Hidden image for PDF rendering if it's an image file */}
                  {field?.fileUrl &&
                  (field.selectedFileName?.toLowerCase().includes('.png') ||
                    field.selectedFileName?.toLowerCase().includes('.jpg') ||
                    field.selectedFileName?.toLowerCase().includes('.jpeg') ||
                    field.selectedFileName?.toLowerCase().includes('.gif')) ? (
                    <img
                      src={field.fileUrl}
                      alt={field.selectedFileName}
                      style={{
                        display: 'none',
                        maxWidth: '100%',
                        height: 'auto',
                      }}
                      crossOrigin='anonymous'
                      className='pdf-image-render'
                    />
                  ) : null}
                </div>
              ) : (
                <p className='text-muted'>No file attached by admin.</p>
              )}
              {field.helpText ? (
                <p style={{ fontSize: '14px' }}>{field.helpText}</p>
              ) : null}
            </div>
          )
        } else {
          // Regular user file upload - keep original logic
          const file =
            files &&
            files.length &&
            files.find((file) => file.field_id === field.index)
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'start',
              }}
            >
              <label
                role='button'
                className='form-control-label'
                style={{ marginBottom: '8px' }}
              >
                {field.label}{' '}
                {field.required ? <span className='text-danger'>*</span> : null}
              </label>
              {file ? (
                <UploadedFile
                  path={file.path}
                  alt={field.label || 'Form attachment'}
                />
              ) : null}
              {field.helpText ? (
                <p style={{ fontSize: '14px' }}>{field.helpText}</p>
              ) : null}
            </div>
          )
        }
      case 'select':
        const options = field.options
          ? field.options.map((field) => {
              return { id: field.id, name: field.label }
            })
          : []
        const selectedOptions = field?.options
          ?.filter((options) => options.checked === true)
          ?.map((field) => {
            return { id: field.id, name: field.label }
          })

        return (
          <>
            <label
              role='button'
              className='form-control-label'
              style={{ marginBottom: '8px' }}
            >
              {field.label}
            </label>
            <Select
              options={options}
              selectedOptions={selectedOptions || []}
              multi='true'
              disabled={true}
            />
          </>
        )
      default:
        return <span>{field.label}</span>
    }
  }

  return (
    <Modal
      toggle={() => {
        dispatch(setShowFormSubmission(false))
        dispatch(setFormSubmissionId(null))
        setShowViewSubmissionModal(false)
      }}
      isOpen={showViewSubmissionModal || showFormSubmission}
      centered
      size='xl'
      // className='scrollbar'
      // style={{ overflowY: 'auto', maxHeight: '90vh' }}
    >
      <CustomModalHeader
        title={
          <div className='d-flex justify-content-between align-items-center w-100'>
            <span>Form Submission</span>
            <Button
              color='primary'
              size='sm'
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}
              className='ml-3'
            >
              <FontAwesomeIcon icon={faDownload} className='mr-2' />
              {isGeneratingPDF ? 'Generating...' : 'Download PDF'}
            </Button>
          </div>
        }
        toggle={() => {
          dispatch(setShowFormSubmission(false))
          dispatch(setFormSubmissionId(null))
          setShowViewSubmissionModal(false)
        }}
      />
      <ModalBody>
        <div className='d-flex justify-content-center align-items-center'>
          <div className='settings-tabs dFlex md-flexCol textCenter gap-10 mb-0 mx-3 py-1 mb-3 '>
            <NavLink
              activeClassName='current'
              className={activeTab === 0 ? 'current' : ''}
              onClick={() => setActiveTab(0)}
            >
              Form
            </NavLink>
            <NavLink
              activeClassName='current'
              className={activeTab === 1 ? 'current' : ''}
              onClick={() => setActiveTab(1)}
            >
              Comments
            </NavLink>
          </div>
        </div>
        <Card className='p-4 d-flex flex-column' style={{ gap: '1rem' }}>
          <>
            {activeTab === 0 ? (
              <div
                ref={formRef}
                key='form-content'
                style={{ display: 'block' }}
              >
                {children}
                {newFormFields?.map((field) => (
                  <div key={field.index} style={{ marginBottom: '1rem' }}>
                    {renderBody(field)}
                  </div>
                ))}
              </div>
            ) : (
              <SubmissionComments submissionId={submissionId} />
            )}
          </>
        </Card>
      </ModalBody>
      <CustomModalFooter>
        <CancelModalButton
          text={'Close'}
          customFn={() => {
            dispatch(setShowFormSubmission(false))

            dispatch(setFormSubmissionId(null))
            setShowViewSubmissionModal(false)
          }}
        ></CancelModalButton>
      </CustomModalFooter>
    </Modal>
  )
}

export default ViewSubmissionModal
